import { useCallback, useEffect } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { GenericErrorDisplay } from '@/common/displays/src/generic-error-display/generic-error-display';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import { getFastshiftAssessmentRecommendationDetails } from '@/state/actions';
import type { AssessmentRecommendationDetail } from '@/state/types';

import { AssessmentResultStatusIcon } from '../assessment-results-section/assessment-result-card';

import { AssessmentRecommendationDetailsContent } from './assessment-recommendation-details-content';
import { assessmentRecommendationDetailsMessages as messages } from './assessment-recommendation-details-messages';
import { useAssessmentRecommendationDetailsAnalytics } from './use-assessment-recommendation-details-analytics';

import type {
	FastshiftAssessmentBlockerStatus,
	FastshiftAssessmentRisk,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

const styles = cssMap({
	panel: {
		minWidth: '0',
		width: '100%',
	},
	centeredState: {
		paddingBlockStart: token('space.600'),
		paddingBlockEnd: token('space.600'),
	},
	errorState: {
		paddingBlockStart: token('space.400'),
	},
	errorDisplay: {
		textAlign: 'center',
	},
	headerTitle: {
		minWidth: '0',
	},
});

const AssessmentRecommendationDetailsPanelBody = ({
	detail,
	onResourceLinkClick,
}: {
	detail: AssessmentRecommendationDetail | undefined;
	onResourceLinkClick: () => void;
}) => {
	const { formatMessage } = useIntl();

	if (!detail || (detail.pending && !detail.data)) {
		return (
			<Inline alignBlock="center" alignInline="center" xcss={styles.centeredState} role="status">
				<Spinner
					size="large"
					label={formatMessage(messages.loadingLabel)}
					interactionName="load-assessment-recommendation-details"
				/>
			</Inline>
		);
	}

	if (detail.error && !detail.data) {
		return (
			<Box role="alert" xcss={styles.errorState}>
				<GenericErrorDisplay
					iconSize="large"
					customMessage={formatMessage(messages.errorMessage)}
					xcss={styles.errorDisplay}
				/>
			</Box>
		);
	}

	return detail.data ? (
		<AssessmentRecommendationDetailsContent
			details={detail.data}
			onResourceLinkClick={onResourceLinkClick}
		/>
	) : null;
};

export type AssessmentRecommendationDetailsPanelProps = {
	domainId: string;
	organizationId: string;
	instanceId: string;
	assessmentId: string;
	recommendationId: string;
	dataPoint: string;
	status: FastshiftAssessmentRisk | null;
	blockerStatus: FastshiftAssessmentBlockerStatus;
	onClose: () => void;
};

export const AssessmentRecommendationDetailsPanel = ({
	domainId,
	organizationId,
	instanceId,
	assessmentId,
	recommendationId,
	dataPoint,
	status,
	blockerStatus,
	onClose,
}: AssessmentRecommendationDetailsPanelProps) => {
	const { formatMessage } = useIntl();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const detail = useFastshiftSelector(
		(state) => state.dcInstanceDetail.recommendationDetailsById[recommendationId],
	);
	const {
		sendRecommendationDetailsDismissClickUIEvent,
		sendRecommendationResourceLinkClickUIEvent,
	} = useAssessmentRecommendationDetailsAnalytics({
		organizationId,
		recommendationId,
		detail,
	});
	const handleClose = useCallback(() => {
		sendRecommendationDetailsDismissClickUIEvent();
		onClose();
	}, [onClose, sendRecommendationDetailsDismissClickUIEvent]);

	const loadDetails = useCallback(() => {
		dispatch(
			getFastshiftAssessmentRecommendationDetails({
				stargateClient,
				domainId,
				orgId: organizationId,
				instanceId,
				assessmentId,
				recommendationId,
			}),
		);
	}, [
		assessmentId,
		dispatch,
		domainId,
		instanceId,
		organizationId,
		recommendationId,
		stargateClient,
	]);

	useEffect(() => {
		if (!detail) {
			loadDetails();
		}
	}, [detail, loadDetails]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleClose();
			}
		};

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleClose]);

	return (
		<Stack space="space.300" grow="fill" xcss={styles.panel}>
			<Inline alignBlock="center" spread="space-between" space="space.150">
				<Inline alignBlock="center" space="space.100" xcss={styles.headerTitle}>
					<AssessmentResultStatusIcon
						status={detail?.data?.summary.impact.status ?? status}
						blockerStatus={detail?.data?.summary.blockerStatus ?? blockerStatus}
					/>
					<Heading size="small" as="h2">
						{detail?.data?.summary.dataPoint ?? dataPoint}
					</Heading>
				</Inline>
				<IconButton
					icon={CrossIcon}
					label={formatMessage(messages.closeLabel)}
					onClick={handleClose}
				/>
			</Inline>

			<AssessmentRecommendationDetailsPanelBody
				detail={detail}
				onResourceLinkClick={sendRecommendationResourceLinkClickUIEvent}
			/>
		</Stack>
	);
};
